// src/pages/Orders.js
import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import toast from 'react-hot-toast';
import { FaShoppingBag, FaTruck, FaUser, FaMapMarkerAlt, FaCreditCard, FaBox, FaTimes, FaCheckCircle, FaClock, FaSpinner, FaBan } from 'react-icons/fa';

const Orders = () => {
  const [orders, setOrders] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [statusUpdate, setStatusUpdate] = useState('');
  const [loading, setLoading] = useState(true);
  const [shippingDetails, setShippingDetails] = useState({
    service: '',
    trackingNumber: '',
    estimatedDelivery: ''
  });

  const fetchOrders = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/orders/all`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed to load');
      const data = await res.json();
      setOrders(data);
    } catch (err) {
      toast.error('Failed to load orders');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, []);

  const openOrderDetails = (order) => {
    setSelectedOrder(order);
    setStatusUpdate(order.status);
    setShippingDetails({
      service: order.shipping?.service || '',
      trackingNumber: order.shipping?.trackingNumber || '',
      estimatedDelivery: order.shipping?.estimatedDelivery ?
        new Date(order.shipping.estimatedDelivery).toISOString().split('T')[0] : ''
    });
    setShowModal(true);
  };

  const handleUpdateOrder = async () => {
    if (!selectedOrder) return;

    try {
      const token = localStorage.getItem('token');
      const payload = {
        status: statusUpdate,
        shipping: {
          service: shippingDetails.service,
          trackingNumber: shippingDetails.trackingNumber,
          estimatedDelivery: shippingDetails.estimatedDelivery ? new Date(shippingDetails.estimatedDelivery) : null
        }
      };

      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/orders/${selectedOrder._id}/status`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (!res.ok) throw new Error('Update failed');

      toast.success('Order updated successfully');
      setShowModal(false);
      fetchOrders();
    } catch (err) {
      toast.error('Failed to update order');
    }
  };

  const getStatusConfig = (status) => {
    switch (status) {
      case 'pending': return {
        bg: 'bg-amber-50',
        text: 'text-amber-700',
        border: 'border-amber-200',
        icon: FaClock,
        label: 'Pending'
      };
      case 'confirmed': return {
        bg: 'bg-blue-50',
        text: 'text-blue-700',
        border: 'border-blue-200',
        icon: FaCheckCircle,
        label: 'Confirmed'
      };
      case 'processing': return {
        bg: 'bg-purple-50',
        text: 'text-purple-700',
        border: 'border-purple-200',
        icon: FaSpinner,
        label: 'Processing'
      };
      case 'shipped': return {
        bg: 'bg-indigo-50',
        text: 'text-indigo-700',
        border: 'border-indigo-200',
        icon: FaTruck,
        label: 'Shipped'
      };
      case 'delivered': return {
        bg: 'bg-emerald-50',
        text: 'text-emerald-700',
        border: 'border-emerald-200',
        icon: FaCheckCircle,
        label: 'Delivered'
      };
      case 'cancelled': return {
        bg: 'bg-red-50',
        text: 'text-red-700',
        border: 'border-red-200',
        icon: FaBan,
        label: 'Cancelled'
      };
      default: return {
        bg: 'bg-slate-50',
        text: 'text-slate-700',
        border: 'border-slate-200',
        icon: FaBox,
        label: status
      };
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-primary-200 border-t-primary-600 rounded-full animate-spin"></div>
          <p className="text-slate-500 font-medium">Loading orders...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <h1 className="text-2xl font-bold text-slate-800">Order Management</h1>
          <p className="text-slate-500 mt-1">{orders.length} total orders</p>
        </motion.div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-8">
          {['pending', 'confirmed', 'processing', 'shipped', 'delivered', 'cancelled'].map((status) => {
            const config = getStatusConfig(status);
            const count = orders.filter(o => o.status === status).length;
            const Icon = config.icon;
            return (
              <motion.div
                key={status}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                className={`${config.bg} ${config.border} border rounded-xl p-4`}
              >
                <div className="flex items-center gap-2 mb-2">
                  <Icon className={`${config.text} text-sm`} />
                  <span className={`text-xs font-medium ${config.text}`}>{config.label}</span>
                </div>
                <p className={`text-2xl font-bold ${config.text}`}>{count}</p>
              </motion.div>
            );
          })}
        </div>

        {/* Orders Grid */}
        <div className="space-y-4">
          {orders.length === 0 ? (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-center py-16 bg-white rounded-xl shadow-card border border-slate-100"
            >
              <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <FaShoppingBag className="text-2xl text-slate-400" />
              </div>
              <h3 className="text-lg font-semibold text-slate-700 mb-2">No orders yet</h3>
              <p className="text-slate-500">Orders will appear here once customers start purchasing</p>
            </motion.div>
          ) : (
            orders.map((order, index) => {
              const statusConfig = getStatusConfig(order.status);
              const StatusIcon = statusConfig.icon;

              return (
                <motion.div
                  key={order._id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  whileHover={{ y: -2 }}
                  onClick={() => openOrderDetails(order)}
                  className="bg-white rounded-xl shadow-card border border-slate-100 overflow-hidden hover:shadow-soft transition-all duration-300 cursor-pointer"
                >
                  {/* Order Header */}
                  <div className="p-5 border-b border-slate-100">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                      <div>
                        <div className="flex items-center gap-3">
                          <h3 className="font-semibold text-slate-800">
                            Order #{order._id.slice(-8).toUpperCase()}
                          </h3>
                          <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold ${statusConfig.bg} ${statusConfig.text} ${statusConfig.border} border`}>
                            <StatusIcon className="text-[10px]" />
                            {statusConfig.label}
                          </span>
                        </div>
                        <p className="text-sm text-slate-500 mt-1">
                          {new Date(order.createdAt).toLocaleDateString('en-IN', {
                            day: 'numeric',
                            month: 'short',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-2xl font-bold text-slate-800">₹{order.totalPrice.toFixed(2)}</p>
                        <p className="text-xs text-slate-500">{order.paymentStatus}</p>
                      </div>
                    </div>
                  </div>

                  {/* Order Body */}
                  <div className="p-5">
                    <div className="grid md:grid-cols-2 gap-6">
                      {/* Customer Details */}
                      <div>
                        <div className="flex items-center gap-2 mb-3">
                          <div className="w-8 h-8 bg-primary-50 rounded-lg flex items-center justify-center">
                            <FaUser className="text-primary-600 text-sm" />
                          </div>
                          <h4 className="font-medium text-slate-700">Customer</h4>
                        </div>
                        <div className="space-y-1.5 text-sm">
                          <p className="text-slate-800 font-medium">{order.customerDetails?.name}</p>
                          <p className="text-slate-500">{order.customerDetails?.email}</p>
                          <p className="text-slate-500">{order.customerDetails?.phone}</p>
                        </div>
                      </div>

                      {/* Shipping Address */}
                      <div>
                        <div className="flex items-center gap-2 mb-3">
                          <div className="w-8 h-8 bg-emerald-50 rounded-lg flex items-center justify-center">
                            <FaMapMarkerAlt className="text-emerald-600 text-sm" />
                          </div>
                          <h4 className="font-medium text-slate-700">Shipping Address</h4>
                        </div>
                        <p className="text-sm text-slate-600 leading-relaxed">
                          {order.customerDetails?.address?.street}, {order.customerDetails?.address?.city}, {order.customerDetails?.address?.state} - {order.customerDetails?.address?.pincode}
                        </p>
                      </div>
                    </div>

                    {/* Products */}
                    <div className="mt-5 pt-5 border-t border-slate-100">
                      <div className="flex items-center gap-2 mb-3">
                        <div className="w-8 h-8 bg-purple-50 rounded-lg flex items-center justify-center">
                          <FaBox className="text-purple-600 text-sm" />
                        </div>
                        <h4 className="font-medium text-slate-700">Products ({order.products?.length})</h4>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {order.products?.slice(0, 3).map((item, idx) => (
                          <div key={idx} className="flex items-center gap-2 bg-slate-50 rounded-lg p-2 pr-4">
                            {item.imageFilename && (
                              <img
                                src={`${process.env.REACT_APP_API_URL}/api/images/${item.imageFilename}`}
                                alt={item.name}
                                className="w-10 h-10 object-cover rounded-md"
                              />
                            )}
                            <div>
                              <p className="text-sm font-medium text-slate-700 line-clamp-1">{item.name}</p>
                              <p className="text-xs text-slate-500">Qty: {item.quantity} × ₹{item.price.toFixed(2)}</p>
                              {item.selectedColor && (
                                <p className="text-xs text-purple-600 font-medium">Color: {item.selectedColor}</p>
                              )}
                            </div>
                          </div>
                        ))}
                        {order.products?.length > 3 && (
                          <div className="flex items-center justify-center bg-slate-100 rounded-lg px-4 text-sm text-slate-600">
                            +{order.products.length - 3} more
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Shipping Info */}
                    {order.shipping?.trackingNumber && (
                      <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-100">
                        <div className="flex items-center gap-2">
                          <FaTruck className="text-blue-600" />
                          <span className="font-medium text-blue-800">{order.shipping.service}</span>
                          <span className="text-blue-600">•</span>
                          <span className="text-blue-700">{order.shipping.trackingNumber}</span>
                        </div>
                      </div>
                    )}
                  </div>
                </motion.div>
              );
            })
          )}
        </div>
      </div>

      {/* Order Update Modal */}
      <AnimatePresence>
        {showModal && selectedOrder && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            onClick={() => setShowModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className="bg-gradient-to-r from-primary-500 to-primary-600 p-6 rounded-t-2xl">
                <div className="flex justify-between items-center">
                  <div>
                    <h2 className="text-xl font-bold text-white">
                      Update Order #{selectedOrder._id.slice(-8).toUpperCase()}
                    </h2>
                    <p className="text-primary-100 text-sm mt-1">
                      {new Date(selectedOrder.createdAt).toLocaleDateString('en-IN', {
                        day: 'numeric',
                        month: 'long',
                        year: 'numeric'
                      })}
                    </p>
                  </div>
                  <button
                    onClick={() => setShowModal(false)}
                    className="w-10 h-10 bg-white/20 hover:bg-white/30 rounded-full flex items-center justify-center transition-colors"
                  >
                    <FaTimes className="text-white" />
                  </button>
                </div>
              </div>

              <div className="p-6 space-y-6">
                {/* Status Update */}
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-3">Order Status</label>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    {['pending', 'confirmed', 'processing', 'shipped', 'delivered', 'cancelled'].map((status) => {
                      const config = getStatusConfig(status);
                      const Icon = config.icon;
                      const isSelected = statusUpdate === status;
                      return (
                        <button
                          key={status}
                          onClick={() => setStatusUpdate(status)}
                          className={`p-3 rounded-xl border-2 transition-all ${
                            isSelected
                              ? `${config.bg} ${config.border} ${config.text}`
                              : 'border-slate-200 hover:border-slate-300 text-slate-600'
                          }`}
                        >
                          <Icon className={`mx-auto mb-1 ${isSelected ? config.text : 'text-slate-400'}`} />
                          <span className="text-sm font-medium">{config.label}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Shipping Details */}
                <div>
                  <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                    <FaTruck className="text-primary-600" />
                    Shipping Details
                  </h3>

                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-600 mb-1.5">Shipping Service</label>
                      <input
                        type="text"
                        value={shippingDetails.service}
                        onChange={(e) => setShippingDetails({ ...shippingDetails, service: e.target.value })}
                        placeholder="e.g., Blue Dart, DTDC, India Post"
                        className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-600 mb-1.5">Tracking Number</label>
                      <input
                        type="text"
                        value={shippingDetails.trackingNumber}
                        onChange={(e) => setShippingDetails({ ...shippingDetails, trackingNumber: e.target.value })}
                        placeholder="Enter tracking number"
                        className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-600 mb-1.5">Estimated Delivery Date</label>
                      <input
                        type="date"
                        value={shippingDetails.estimatedDelivery}
                        onChange={(e) => setShippingDetails({ ...shippingDetails, estimatedDelivery: e.target.value })}
                        className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-800 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
                      />
                    </div>
                  </div>
                </div>

                {/* Order Information */}
                <div>
                  <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                    <FaCreditCard className="text-primary-600" />
                    Payment Information
                  </h3>
                  <div className="bg-slate-50 p-4 rounded-xl space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-500">Payment ID</span>
                      <span className="text-slate-700 font-medium">{selectedOrder.paymentId || 'N/A'}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-500">Payment Status</span>
                      <span className={`font-medium ${selectedOrder.paymentStatus === 'completed' ? 'text-emerald-600' : 'text-amber-600'}`}>
                        {selectedOrder.paymentStatus}
                      </span>
                    </div>
                    {selectedOrder.shipping?.shippedAt && (
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-500">Shipped At</span>
                        <span className="text-slate-700">{new Date(selectedOrder.shipping.shippedAt).toLocaleString()}</span>
                      </div>
                    )}
                    {selectedOrder.shipping?.deliveredAt && (
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-500">Delivered At</span>
                        <span className="text-slate-700">{new Date(selectedOrder.shipping.deliveredAt).toLocaleString()}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-3 pt-2">
                  <button
                    onClick={handleUpdateOrder}
                    className="flex-1 bg-gradient-to-r from-primary-500 to-primary-600 text-white px-6 py-3 rounded-xl font-semibold hover:from-primary-600 hover:to-primary-700 transition-all shadow-lg shadow-primary-500/30"
                  >
                    Update Order
                  </button>
                  <button
                    onClick={() => setShowModal(false)}
                    className="flex-1 bg-slate-100 text-slate-700 px-6 py-3 rounded-xl font-semibold hover:bg-slate-200 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Orders;
